import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthServiceService } from '../../core/services/authService/auth-service.service';
import { TokenExpirePayload, TokenExpireResponse } from '../../core/models/interfaces/login';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { jwtDecode } from 'jwt-decode';

let isRefreshing = false;
let refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const AuthInterceptor: HttpInterceptorFn = (
	req: HttpRequest<any>,
	next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
	const router = inject(Router);
	const authService = inject(AuthServiceService);
	const toastr = inject(ToastrService);
	const dialog = inject(MatDialog);
	const accessToken = localStorage.getItem('token');
	const refreshToken = localStorage.getItem('refresh_token');

	const publicEndpoints = [
		'/userManagement/signup',
		'/userManagement/forgot-password',
		'/userManagement/new-password',
		'/userManagement/otp',
		'/userManagement/messge',
		'/auth-callback',
		'/userManagement/refresh',
		'/company/register/',
		'/jobs/public/'
	];

	// External HRMS backends use their own auth — skip MindMentor token injection and error handling
	const externalDomains = ['dev-backend.acelucid.com', 'dev-hrmsai.acelucid.com'];

	const isPublic = publicEndpoints.some(endpoint => req.url.includes(endpoint))
		|| externalDomains.some(domain => req.url.includes(domain));
	if (isPublic) return next(req);

	if (accessToken && refreshToken && isTokenExpiringSoon(accessToken)) {
		if (isRefreshing) {
			return refreshTokenSubject.pipe(
				filter(token => token !== null),
				take(1),
				switchMap(token => next(req.clone({
					setHeaders: { Authorization: `Bearer ${token}` }
				})))
			);
		}
		return doRefresh(req, next, authService, router, toastr, dialog, refreshToken);
	}

	const authReq = accessToken
		? req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } })
		: req;

	return next(authReq).pipe(
		catchError((error: HttpErrorResponse) => {
			// 403 = permission denied (not an auth issue) — refreshing the token won't help, let it propagate
			if (error.status !== 401) return throwError(() => error);

			const currentRefreshToken = localStorage.getItem('refresh_token');
			if (!currentRefreshToken) return handleLogout(router, toastr, dialog, authService);

			if (isRefreshing) {
				return refreshTokenSubject.pipe(
					filter(token => token !== null),
					take(1),
					switchMap(token => next(req.clone({
						setHeaders: { Authorization: `Bearer ${token}` }
					})))
				);
			}
			return doRefresh(req, next, authService, router, toastr, dialog, currentRefreshToken);
		})
	);
};


function doRefresh(
	req: HttpRequest<any>,
	next: HttpHandlerFn,
	authService: AuthServiceService,
	router: Router,
	toastr: ToastrService,
	dialog: MatDialog,
	refreshToken: string
): Observable<HttpEvent<any>> {
	isRefreshing = true;
	refreshTokenSubject.next(null);

	return authService.refreshToken({ refresh_token: refreshToken }).pipe(
		switchMap((res: TokenExpireResponse) => {
			const newAccessToken = res?.Data?.access_token;
			const newRefreshToken = res?.Data?.refresh_token;

			if (!newAccessToken) return handleLogout(router, toastr, dialog, authService);

			localStorage.setItem('token', newAccessToken);
			if (newRefreshToken) {
				localStorage.setItem('refresh_token', newRefreshToken);
			}

			isRefreshing = false;
			refreshTokenSubject.next(newAccessToken);

			return next(req.clone({
				setHeaders: { Authorization: `Bearer ${newAccessToken}` }
			}));
		}),
		catchError(() => {
			isRefreshing = false;
			refreshTokenSubject.next(null);
			return handleLogout(router, toastr, dialog, authService);
		})
	);
}



function isTokenExpiringSoon(token: string): boolean {
	try {
		const decoded: any = jwtDecode(token);
		const timeLeft = decoded.exp * 1000 - Date.now();
		return timeLeft < 60 * 1000;
	} catch {
		return true;
	}
}


function handleLogout(router: Router, toastr: ToastrService, dialog: MatDialog, authService: AuthServiceService) {
	dialog.closeAll();
	isRefreshing = false;
	refreshTokenSubject.next(null);
	toastr.info('Your session has expired. Please log in again.', 'Session Expired');
	authService.performLogoutCleanup(router);
	return throwError(() => new Error('Session expired'));
}